// An engagement model's thresholds, as a volume, on the Event weights page.
//
// Fit has trees to weigh and a page of its own to weigh them on; an engagement
// model has one score, and what moves it is the event weights. So the tiering
// sits with them: the handles on the share of leads, the score grid the server
// builds from the SAVED weights as the ladder they move along, and the point
// threshold each lands on typed under it. After a weights save the page bumps
// `revision`, this re-reads the grid, and the handles keep their shares: a
// changed weight visibly moves the point thresholds rather than the volumes.
//
// The ladder and panel are the fit page's, translated at this edge
// (`engagementThresholds.ts`). A drag is one debounced write; Compute
// performance is the rebuild the server charges for.
import { inject, observer } from 'mobx-react';
import React from 'react';
import { Button, Card } from 'react-bootstrap';

import { PROFILES_PERMISSIONS } from '../../../permissions';
import Store from '../../../store';
import checkPermissions from '../CheckPermissions';
import Error from '../error/Error';
import Spinner from '../Spinner';
import {
  AUTO_SELECT_BOUNDS,
  relabelTiers,
  wholePointGrid,
  toEngagementKeys,
  toFitKeys,
} from './engagementThresholds';
import { fetchScoreGrid } from './leafGridApi';
import ModelPerformanceBands from './ModelPerformanceBands';
import {
  PointsGrid,
  boundsFromThresholds,
  pointsLadder,
  thresholdsFromBounds,
  tiersFromThresholds,
} from './scoreLadder';
import { unitOf } from './segmentVocabulary';
import TieringPanel, { MIN_VISIBLE } from './TieringPanel';
import '../../pages/model/fineTuning.css';

/** As on the fit page: a drag is one write, not forty. */
const SAVE_DELAY_MS = 600;

interface Props {
  store?: Store;
  /** Bumped by the page after the weights are saved: re-read the grid. */
  revision: number;
}

interface State {
  isLoading: boolean;
  loadError: string | null;
  isSaving: boolean;
  grid: PointsGrid | null;
  /** The requested cumulative shares, best first. See FineTuningPage. */
  bounds: number[] | null;
  isStale: boolean;
  isOpen: boolean;
}

class EngagementTiering extends React.Component<Props, State> {
  saveTimer: ReturnType<typeof setTimeout> | null = null;

  mounted = false;

  constructor(props: Props) {
    super(props);
    this.state = {
      isLoading: true,
      loadError: null,
      isSaving: false,
      grid: null,
      bounds: null,
      isStale: false,
      isOpen: true,
    };
    this.changeBounds = this.changeBounds.bind(this);
    this.compute = this.compute.bind(this);
  }

  async componentDidMount() {
    this.mounted = true;
    const { tenant, modelId, ensemblingPage } = this.props.store;
    try {
      await ensemblingPage.loadConfiguration(tenant, modelId);
      await this.loadGrid(null);
    } catch (error) {
      if (this.mounted)
        this.setState({ loadError: error?.message ?? String(error) });
    } finally {
      if (this.mounted) this.setState({ isLoading: false });
    }
  }

  async componentDidUpdate(previous: Props) {
    if (previous.revision !== this.props.revision) {
      // The weights changed the scores; the volumes asked for stay, so the
      // thresholds are re-derived from them on the new ladder and written.
      await this.loadGrid(this.state.bounds);
    }
  }

  componentWillUnmount() {
    this.mounted = false;
    if (this.saveTimer) clearTimeout(this.saveTimer);
  }

  async loadGrid(keepBounds: number[] | null) {
    const { tenant, modelId, ensemblingPage } = this.props.store;
    const grid = await fetchScoreGrid(tenant, modelId);
    if (!this.mounted) return;
    const ladder = pointsLadder(grid);
    this.setState({ grid });
    if (!ladder.length) {
      this.setState({ bounds: null });
      return;
    }
    if (keepBounds) {
      this.setState({ grid }, () => this.changeBounds(keepBounds));
      return;
    }
    this.setState({
      bounds: boundsFromThresholds(
        toFitKeys(ensemblingPage.ensemble?.thresholds),
        ladder
      ),
    });
  }

  /** A drag, Auto-select or a re-derivation after a weights save: always on
   *  whole points. Only a threshold somebody types carries decimals. */
  changeBounds(bounds: number[]) {
    const ladder = pointsLadder(wholePointGrid(this.state.grid));
    const derived = thresholdsFromBounds(bounds, ladder);
    if (!derived) return;
    this.props.store.ensemblingPage.ensemble.thresholds =
      toEngagementKeys(derived);
    this.setState({ bounds, isStale: true });
    this.scheduleSave();
  }

  scheduleSave() {
    if (this.saveTimer) clearTimeout(this.saveTimer);
    this.saveTimer = setTimeout(() => {
      this.saveTimer = null;
      void this.save();
    }, SAVE_DELAY_MS);
  }

  async save() {
    if (!this.mounted) return;
    this.setState({ isSaving: true });
    await this.props.store.saveEnsemblingConfiguration();
    if (this.mounted) this.setState({ isSaving: false });
  }

  async compute() {
    if (this.saveTimer) {
      clearTimeout(this.saveTimer);
      this.saveTimer = null;
    }
    await this.props.store.loadEnsemblingPerformances({ isProcessing: false });
    if (this.mounted) this.setState({ isStale: false });
  }

  render() {
    const { ensemblingPage, activeModel, isAllowedToEdit } = this.props.store;
    const { ensemble, loadingEnsemblingPerformances, ensemblingError } =
      ensemblingPage;
    const { isLoading, loadError, grid, bounds, isSaving, isStale, isOpen } =
      this.state;

    if (isLoading) return <Spinner />;
    if (loadError) return <Error message={loadError} />;
    if (!ensemble) return null;

    const unit = unitOf(activeModel.type);
    const ladder = pointsLadder(grid);
    // What a handle may land on: whole points. The tiers are still cut on the
    // exact ladder, so a typed 5.35 reads true.
    const dragLadder = pointsLadder(wholePointGrid(grid));
    const tiers =
      bounds && ladder.length
        ? relabelTiers(
            tiersFromThresholds(toFitKeys(ensemble.thresholds), ladder)
          )
        : null;
    const computed = ensemble.behavioralPerformances?.performances ?? null;

    return (
      <>
        {ensemblingError && <Error message={ensemblingError} />}
        <Card className="ftune-card">
          <Card.Body>
            <div
              className={`ftune-actions ${
                isOpen ? '' : 'ftune-actions--closed'
              }`}
            >
              <button
                type="button"
                className="tpf-toggle"
                aria-expanded={isOpen}
                onClick={() => this.setState({ isOpen: !isOpen })}
              >
                <span className="tpf-chevron" aria-hidden="true">
                  {isOpen ? '▼' : '►'}
                </span>
                <span className="tpf-tree">Tiering</span>
              </button>
              <span style={{ flex: '1 1 0' }} />
              {checkPermissions(
                PROFILES_PERMISSIONS.ARCHITECT,
                <>
                  <Button
                    size="sm"
                    variant="light"
                    className="ftune-btn-quiet"
                    data-testid="tuning-auto-select"
                    disabled={!isAllowedToEdit || !tiers}
                    onClick={() =>
                      this.changeBounds(AUTO_SELECT_BOUNDS.slice())
                    }
                    title={`Very high for the top 10% of ${unit}, high for the next 20%, medium for the next 20%.`}
                  >
                    Auto-select
                  </Button>
                  <Button
                    size="sm"
                    variant="primary"
                    data-testid="tuning-compute"
                    disabled={!isAllowedToEdit || loadingEnsemblingPerformances}
                    onClick={this.compute}
                  >
                    {loadingEnsemblingPerformances
                      ? 'Computing...'
                      : 'Compute performance'}
                  </Button>
                </>
              )}
              <span
                className={`ftune-saving ${
                  isSaving ? '' : 'ftune-saving--idle'
                }`}
              >
                Saving...
              </span>
            </div>
            {isOpen &&
              (tiers && bounds ? (
                <TieringPanel
                  tiers={tiers}
                  bounds={bounds}
                  ladder={dragLadder}
                  minVisible={MIN_VISIBLE}
                  onChangeBounds={this.changeBounds}
                  disabled={!isAllowedToEdit}
                  thresholdInput={{
                    step: 0.01,
                    suffix: 'pts',
                    onChange: (segment, value) => {
                      const next = { ...toFitKeys(ensemble.thresholds) };
                      next[segment] = value;
                      ensemble.thresholds = toEngagementKeys(next);
                      this.setState({
                        bounds: boundsFromThresholds(next, ladder),
                        isStale: true,
                      });
                      this.scheduleSave();
                    },
                  }}
                />
              ) : (
                <p className="ftune-hint">
                  No scores to tier on yet: the datasets are not loaded, or no
                  event carries a weight.
                </p>
              ))}
            {isOpen && isStale && computed && (
              <p className="ftune-stale">
                The thresholds changed since the performance below was computed.
              </p>
            )}
          </Card.Body>
        </Card>
        {isOpen && computed && (
          <div className="ftune-card">
            <ModelPerformanceBands
              performances={computed}
              vocabulary="behavioral"
              unit={unit}
              title="Performance"
              scope="Training set, thresholds as last computed"
            />
          </div>
        )}
      </>
    );
  }
}

export default inject('store')(observer(EngagementTiering));
